import React from "react";
import { Grid, Card, Box, Stack, Avatar, Chip } from "@mui/material";
import {
  TrendingUp,
  TrendingDown,
  ChevronRight,
  Clock,
  Shield,
  Sparkles,
} from "lucide-react";
import MDTypography from "components/MDTypography";

// ─── Stat Card ────────────────────────────────────────────────────────────────
export const StatCard = ({
  label,
  value,
  icon: Icon,
  color,
  trend,
  trendLabel,
  isGradient,
}) => (
  <Card
    className={`stat-card ${isGradient ? "stat-card--gradient" : "stat-card--plain"}`}
  >
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="flex-start"
    >
      <Box>
        <MDTypography className="stat-card__label">{label}</MDTypography>
        <MDTypography className="stat-card__value">{value}</MDTypography>
        {trendLabel && (
          <div className="stat-card__trend">
            {trend === "up" ? (
              <TrendingUp
                size={13}
                color={isGradient ? "rgba(255,255,255,0.75)" : "#22c55e"}
              />
            ) : (
              <TrendingDown
                size={13}
                color={isGradient ? "rgba(255,255,255,0.75)" : "#ef4444"}
              />
            )}
            <MDTypography className={`stat-card__trend-label ${trend}`}>
              {trendLabel}
            </MDTypography>
          </div>
        )}
      </Box>
      <Box
        className="stat-card__icon-box"
        style={isGradient ? {} : { background: `${color}15`, color }}
      >
        <Icon size={22} />
      </Box>
    </Stack>
  </Card>
);

// ─── Quick Action Card ────────────────────────────────────────────────────────
export const QuickAction = ({
  title,
  subtitle,
  icon: Icon,
  color,
  onClick,
  badge,
}) => (
  <Card
    className="quick-action"
    onClick={onClick}
    sx={{
      "&:hover": {
        borderColor: `${color}55`,
        "& .qa-icon-inner": {
          background: `${color} !important`,
          color: "#fff !important",
          transform: "scale(1.08)",
        },
      },
    }}
  >
    <div
      className="quick-action__bar"
      style={{ background: `linear-gradient(90deg, ${color}, ${color}66)` }}
    />
    <Stack direction="row" alignItems="center" spacing={2}>
      <Box
        className="quick-action__icon qa-icon-inner"
        style={{ background: `${color}12`, color }}
      >
        <Icon size={22} />
      </Box>
      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <MDTypography className="quick-action__title">{title}</MDTypography>
          {badge && (
            <Chip
              label={badge}
              size="small"
              className="quick-action__badge"
              style={{
                backgroundColor: `${color}18`,
                color,
                border: `1px solid ${color}30`,
              }}
            />
          )}
        </Stack>
        {subtitle && (
          <MDTypography className="quick-action__subtitle">
            {subtitle}
          </MDTypography>
        )}
      </Box>
      <div className="quick-action__arrow">
        <ChevronRight size={17} />
      </div>
    </Stack>
  </Card>
);

// ─── Section Label ────────────────────────────────────────────────────────────
export const SectionLabel = ({ children, icon: Icon }) => (
  <div className="section-label">
    {Icon && <Icon size={14} className="section-label__icon" />}
    <MDTypography className="section-label__text">{children}</MDTypography>
    <div className="section-label__line" />
  </div>
);

// ─── Welcome Header ───────────────────────────────────────────────────
export const WelcomeHeader = ({ User, showId, currentTime }) => (
  <Card className="welcome-header" elevation={0}>
    <div className="welcome-header__inner">
      <div className="welcome-header__left">
        <Avatar className="welcome-header__avatar">
          {User.name?.charAt(0)?.toUpperCase()}
        </Avatar>
        <Box>
          <MDTypography className="welcome-header__name">
            Good day, {User.name?.split(" ")[0]} 👋
          </MDTypography>
          <div className="welcome-header__chips">
            <Chip
              icon={<Shield size={11} style={{ color: "var(--medco-blue)" }} />}
              label={User.userType}
              size="small"
              className="welcome-header__chip-role"
            />
            {showId && (
              <Chip
                label={`ID: ${User.id}`}
                size="small"
                className="welcome-header__chip-id"
              />
            )}
          </div>
        </Box>
      </div>
      <div className="welcome-header__right">
        {currentTime && (
          <div className="welcome-header__clock">
            <Clock size={13} className="welcome-header__clock-icon" />
            <span>{currentTime}</span>
          </div>
        )}
        <Chip
          icon={<Sparkles size={12} style={{ color: "#d97706" }} />}
          label="Active Session"
          size="small"
          className="welcome-header__chip-session"
        />
      </div>
    </div>
  </Card>
);
